import React, { useState } from 'react';
import 'react-lazy-load-image-component/src/effects/blur.css';

import SquareCard from '../../components/SquareCard';
import ExperienceSummary from '../../components/ExperienceSummary';
import { useForcedCartCache } from '../../context/CartCacheContext';
import { mapBy } from '../../../../../../../../shared/lib/functional';
import { useForcedEventCache } from '../../context/EventCacheContext';
import { StepPropsBase } from '../Reserve';
import Alert from '../../components/Alert';
import Pagination from '../../components/Pagination';
import TwoColumnLayout from '../../components/TwoColumnLayout';
import { useApp } from '../../context/AppContext';
import { evaluateAudienceZone } from '../../../../../../../../shared/lib/audience';

type PackagesViewProps = StepPropsBase;

export default function PackagesView(props: PackagesViewProps) {
  const { onBack, onNext } = props;

  const { event, allEvents } = useForcedEventCache();
  const { activeDraft: activeDraft, passiveUpdatePackage, saveCart } = useForcedCartCache();
  const { dynamicConstants } = useApp();
  const { isMobile } = dynamicConstants;

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const draft = activeDraft.reservationDraft;
  const draftTemplate = draft.eventSnapshot;

  const ticketsSum = (draft.tickets ?? []).map(t => t.quantity).reduce((a, b) => a + b, 0);
  const availablePackages = draftTemplate?.reservables.packages ?? [];
  const itemsPerPage = 12;
  const totalPages = Math.ceil(availablePackages.length / itemsPerPage);
  const paginatedPackages = availablePackages.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const myPackages = draft.packages ?? [];

  const myPackagesDict = mapBy(myPackages, p => p.package._id);
  const eventPackages = mapBy(event.eventTemplate?.reservables.packages ?? [], '_id');

  async function handleOnNext() {
    setLoading(true);

    try {
      await saveCart();
      onNext?.();
    } finally {
      setLoading(false);
    }
  }

  const dataCollections = allEvents?.collections ?? {
    segments: [],
    levels: [],
    attributes: [],
    zones: [],
  };

  const visibleZones = dataCollections.zones
    .filter(z => evaluateAudienceZone('visible', z._id, undefined, event.eventTemplate.audience))
    .map(z => (
      <div
        key={z._id}
        className="text-white shrink-0 min-w-0 bg-black text-sm font-bold rounded-full px-2 py-0 truncate go-event-zone"
        style={{ backgroundColor: z.color }}
      >
        {z.name}
      </div>
    ));

  return (
    <TwoColumnLayout
      title={draftTemplate?.title}
      tags={visibleZones}
      subtitle="Packages"
      aside={
        <ExperienceSummary onBack={() => onBack?.()} onNext={handleOnNext} loading={loading} />
      }
    >
      {availablePackages.length > 0 ? (
        <>
          <div
            className={`grid gap-4 grid-cols-1 ${isMobile ? '' : 'md:grid-cols-2 lg:grid-cols-3'}`}
          >
            {paginatedPackages.map(p => (
              <SquareCard
                key={p._id}
                name={p.name}
                description={p.description}
                price={p.price * (p.pricePerTicket ? ticketsSum : 1)}
                image={eventPackages[p._id]?.images[0]}
                amount={myPackagesDict[p._id]?.quantity}
                setAmount={amt => passiveUpdatePackage(p, amt)}
              />
            ))}
          </div>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPage={setCurrentPage} />
        </>
      ) : (
        <Alert>No packages available for this experience. You can continue to the next step.</Alert>
      )}
    </TwoColumnLayout>
  );
}
